import { GrElement, TOKENS_CSS } from "../core/base";

/**
 * <gr-slider topic="matrix/brightness" label="Brightness" min="0" max="255"
 *            step="1"></gr-slider>
 *
 * Publishes the numeric value on input (throttled to one message per
 * animation frame so dragging doesn't flood a serial link).
 */
export class GrSlider extends GrElement {
  private pendingValue: number | null = null;
  private flushScheduled = false;

  connectedCallback(): void {
    const source = this.publisherSource("gr-slider");
    const root = this.attachShadow({ mode: "open" });
    root.innerHTML = `
      <style>
        ${TOKENS_CSS}
        :host {
          display: flex;
          flex-direction: column;
          gap: 10px;
          padding: 16px 18px;
          border-radius: var(--_radius);
          background: var(--_bg);
          border: 1px solid var(--_border);
          min-width: 180px;
        }
        .row { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; }
        .value { font-variant-numeric: tabular-nums; font-weight: 600; font-size: 15px; }
        input[type="range"] {
          -webkit-appearance: none;
          appearance: none;
          width: 100%;
          height: 4px;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.12);
          outline: none;
        }
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 16px;
          height: 16px;
          border-radius: 999px;
          background: var(--_accent);
          cursor: pointer;
          border: none;
        }
        input[type="range"]::-moz-range-thumb {
          width: 16px;
          height: 16px;
          border-radius: 999px;
          background: var(--_accent);
          cursor: pointer;
          border: none;
        }
      </style>
      <div class="row"><span class="label"></span><span class="value"></span></div>
      <input type="range" />
    `;
    const input = root.querySelector<HTMLInputElement>("input")!;
    const valueEl = root.querySelector(".value")!;
    input.min = this.attr("min", "0");
    input.max = this.attr("max", "100");
    input.step = this.attr("step", "1");
    input.value = this.attr("value", input.min);
    root.querySelector(".label")!.textContent = this.attr("label", this.attr("topic"));
    valueEl.textContent = input.value;

    input.addEventListener("input", () => {
      valueEl.textContent = input.value;
      this.pendingValue = Number(input.value);
      if (this.flushScheduled) return;
      this.flushScheduled = true;
      requestAnimationFrame(() => {
        this.flushScheduled = false;
        if (this.pendingValue !== null) {
          this.bus.publish(this.attr("topic"), this.pendingValue, { src: source });
          this.pendingValue = null;
        }
      });
    });

    this.track(
      this.bus.subscribe(this.attr("topic"), (msg) => {
        if (msg.src === source) return;
        const num = Number(msg.payload);
        if (Number.isNaN(num)) return;
        input.value = String(num);
        valueEl.textContent = input.value;
      }),
    );
  }
}

customElements.define("gr-slider", GrSlider);
